// Voice Memo v2 (companion) — record with pause/resume, a live waveform, and a Mail/Task
// destination toggle (remembers your last choice). Tapping Send uploads the audio and returns
// immediately (202); transcribe → LLM clean-up + subject/title → deliver runs server-side in
// voicememo-process-background. Controls: ✕ cancel · ● record/pause · ➤ send.
import { goHome, toast } from "./app";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type VmState = "idle" | "recording" | "paused" | "sending";
type Dest = "mail" | "task";

let media: MediaRecorder | null = null;
let chunks: BlobPart[] = [];
let stream: MediaStream | null = null;
let curFmt = "webm";
let dest: Dest = "mail";
let pendingSend = false;

// timer (paused-aware): accMs = finished segments, segStart = current segment's start
let accMs = 0;
let segStart = 0;
let timerId: number | null = null;

// waveform
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let waveRAF: number | null = null;

const MAX_MS = 5 * 60 * 1000;
const canPause = typeof MediaRecorder !== "undefined" && typeof MediaRecorder.prototype.pause === "function";

const setVm = (s: VmState): void => { $("#vm")?.setAttribute("data-vm", s); };
const state = (): VmState => ($("#vm")?.getAttribute("data-vm") as VmState) || "idle";
const elapsed = (): number => accMs + (segStart ? Date.now() - segStart : 0);

function fmtClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
function localStamp(): string {
  const d = new Date();
  const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
  return `${d.getDate()} ${mon}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function pickMime(): { mime: string; fmt: string } {
  const cands = [
    { mime: "audio/webm;codecs=opus", fmt: "webm" },
    { mime: "audio/webm", fmt: "webm" },
    { mime: "audio/mp4", fmt: "mp4" },
    { mime: "audio/ogg;codecs=opus", fmt: "ogg" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", fmt: "webm" };
}

const setHint = (txt: string): void => { const h = $("#vmHint"); if (h) h.textContent = txt; };
const showErr = (txt: string): void => { const e = $("#vmErr"); if (e) { e.textContent = txt; e.hidden = false; } };
const clearErr = (): void => { const e = $("#vmErr"); if (e) e.hidden = true; };

// ── destination toggle (persisted) ──
function setDest(d: Dest): void {
  dest = d;
  localStorage.setItem("rplay-vm-dest", d);
  document.querySelectorAll<HTMLElement>("#vmDest .vm-dest-opt").forEach((b) => b.classList.toggle("on", b.getAttribute("data-dest") === d));
}

// ── record / pause / resume (one button) ──
export async function vmToggle(): Promise<void> {
  const s = state();
  if (s === "idle") return void startRec();
  if (s === "recording") return pauseRec();
  if (s === "paused") return resumeRec();
}

async function startRec(): Promise<void> {
  clearErr();
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    showErr("We couldn't access your microphone. Check the permission and try again.");
    return;
  }
  const { mime, fmt } = pickMime();
  curFmt = fmt;
  chunks = [];
  try { media = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
  catch { media = new MediaRecorder(stream); }
  media.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
  media.onstop = () => void onStop();
  media.start();
  accMs = 0; segStart = Date.now();
  setVm("recording");
  setHint(canPause ? "Recording — tap to pause" : "Recording…");
  startTimer();
  startWave();
}

function pauseRec(): void {
  if (!canPause || !media || media.state !== "recording") return;
  try { media.pause(); } catch { return; }
  accMs += Date.now() - segStart; segStart = 0;
  stopWave();
  freezeWavePaused(); // recolour the frozen bars (lighter orange)
  setVm("paused");
  setHint("Paused — tap to resume");
}

function resumeRec(): void {
  if (!media || media.state !== "paused") return;
  try { media.resume(); } catch { return; }
  segStart = Date.now();
  setVm("recording");
  setHint("Recording — tap to pause");
  startWave();
}

// ── send (fire-and-forget; server processes in the background) ──
async function vmSend(): Promise<void> {
  const s = state();
  if (s === "idle" || s === "sending") return;
  if (elapsed() < 700) { toast("That was too short — keep talking."); return; }
  if (segStart) { accMs += Date.now() - segStart; segStart = 0; }
  stopTimer();
  pendingSend = true;
  setVm("sending");
  try { media?.stop(); } catch { void onStop(); } // onStop does the upload
}

async function onStop(): Promise<void> {
  if (!pendingSend) return; // a cancel — discard silently
  pendingSend = false;
  const blob = new Blob(chunks);
  chunks = [];
  releaseStream();
  teardownAudio();
  try {
    const b64 = await blobToB64(blob);
    const r = await fetch("/api/voicememo", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ audio_b64: b64, audio_format: curFmt, dest, stamp: localStamp() }),
    });
    if (r.status === 202 || r.ok) {
      toast(dest === "task" ? "Sent — it'll appear in Tasks shortly" : "Sent — we'll process it in the background");
      resetIdle();
      goHome();
      return;
    }
    const j = (await r.json().catch(() => ({}))) as { error?: string };
    resetIdle();
    showErr(j.error === "not_linked" ? "This phone isn't linked. Reconnect from Outlook." : j.error === "audio_too_large" ? "That recording is too long to send." : "Couldn't send — try again.");
  } catch {
    resetIdle();
    showErr("Couldn't send — check your connection and try again.");
  }
}

// ── cancel (discard + leave) ──
function vmCancel(): void {
  pendingSend = false;
  cleanup();
  resetIdle();
  goHome();
}

// ── timer ──
function startTimer(): void {
  if (timerId) clearTimeout(timerId);
  const tick = (): void => {
    const remaining = Math.max(0, MAX_MS - elapsed()); // counts down from 5:00
    const t = $("#vmTimer"); if (t) t.textContent = fmtClock(remaining);
    if (remaining <= 0 && state() === "recording") { pauseRec(); setHint("Max length reached — send or cancel"); }
    timerId = window.setTimeout(tick, 250);
  };
  tick();
}
function stopTimer(): void { if (timerId) { clearTimeout(timerId); timerId = null; } }

// ── waveform (Web Audio AnalyserNode → symmetric, centre-out bars) ──
const BARS_HALF = 22; // bars per side → 44 total, mirrored around the centre line
let lastLevels: number[] = [];

function ctxOf(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D | null; w: number; h: number } {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth || 460, h = canvas.clientHeight || 120;
  const pw = Math.round(w * dpr), ph = Math.round(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) { canvas.width = pw; canvas.height = ph; }
  const ctx = canvas.getContext("2d");
  if (ctx) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(dpr, dpr); }
  return { ctx, w, h };
}
function bar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const r = Math.min(w / 2, h / 2);
  if ((ctx as any).roundRect) { ctx.beginPath(); (ctx as any).roundRect(x, y, w, h, r); ctx.fill(); }
  else ctx.fillRect(x, y, w, h);
}
const REC_RED = "#EF4444";       // waveform while recording (matches the red mic)
const PAUSE_ORANGE = "#F97316";  // frozen waveform while paused (matches the orange pause)

// Mirror levels around the centre line: levels[0] is the centre (loudest), tapering to the edges.
function paintBars(canvas: HTMLCanvasElement, levels: number[], color: string): void {
  const { ctx, w, h } = ctxOf(canvas);
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = color;
  const half = levels.length, bw = w / (half * 2), cx = w / 2, mid = h / 2, iw = bw * 0.56;
  for (let j = 0; j < half; j++) {
    const bh = Math.max(3, levels[j] * h * 0.9);
    bar(ctx, cx + j * bw + (bw - iw) / 2, mid - bh / 2, iw, bh);        // right of centre
    bar(ctx, cx - (j + 1) * bw + (bw - iw) / 2, mid - bh / 2, iw, bh);  // mirror to the left
  }
}
function startWave(): void {
  const canvas = $("#vmWave") as HTMLCanvasElement | null;
  if (!canvas || !stream) return;
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const src = audioCtx.createMediaStreamSource(stream);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
  }
  void audioCtx.resume?.();
  if (!analyser) return;
  const bins = analyser.frequencyBinCount, usable = Math.min(bins, 96);
  const data = new Uint8Array(bins);
  const draw = (): void => {
    waveRAF = requestAnimationFrame(draw);
    analyser!.getByteFrequencyData(data);
    const levels: number[] = [];
    for (let j = 0; j < BARS_HALF; j++) levels.push(data[Math.floor((j / BARS_HALF) * usable)] / 255);
    lastLevels = levels;
    paintBars(canvas, levels, REC_RED);
  };
  if (waveRAF) cancelAnimationFrame(waveRAF);
  draw();
}
function stopWave(): void { if (waveRAF) { cancelAnimationFrame(waveRAF); waveRAF = null; } }
function freezeWavePaused(): void {
  const canvas = $("#vmWave") as HTMLCanvasElement | null;
  if (canvas && lastLevels.length) paintBars(canvas, lastLevels, PAUSE_ORANGE);
}
function drawIdleWave(): void {
  const canvas = $("#vmWave") as HTMLCanvasElement | null;
  if (!canvas) return;
  const { ctx, w, h } = ctxOf(canvas);
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "rgba(140,150,170,0.28)";
  const half = BARS_HALF, bw = w / (half * 2), cx = w / 2, mid = h / 2, iw = bw * 0.56;
  for (let j = 0; j < half; j++) {
    bar(ctx, cx + j * bw + (bw - iw) / 2, mid - 1.5, iw, 3);
    bar(ctx, cx - (j + 1) * bw + (bw - iw) / 2, mid - 1.5, iw, 3);
  }
}
function teardownAudio(): void {
  stopWave();
  try { void audioCtx?.close(); } catch { /* ignore */ }
  audioCtx = null; analyser = null;
}

// ── helpers / lifecycle ──
function releaseStream(): void { stream?.getTracks().forEach((t) => t.stop()); stream = null; }
function cleanup(): void {
  stopTimer(); teardownAudio();
  try { if (media && media.state !== "inactive") media.stop(); } catch { /* ignore */ }
  releaseStream();
  media = null; chunks = []; accMs = 0; segStart = 0; lastLevels = [];
}
function resetIdle(): void {
  setVm("idle");
  const t = $("#vmTimer"); if (t) t.textContent = fmtClock(MAX_MS); // 5:00 — counts down while recording
  setHint("Tap the mic to start recording");
  requestAnimationFrame(drawIdleWave);
}
function blobToB64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => { const s = String(fr.result || ""); const i = s.indexOf(","); resolve(i >= 0 ? s.slice(i + 1) : s); };
    fr.onerror = () => reject(new Error("read_failed"));
    fr.readAsDataURL(blob);
  });
}

// Reset to a clean idle state — called when entering the tool (shell.ts).
export function vmReset(): void {
  cleanup();
  clearErr();
  resetIdle();
}

export function initVoiceMemo(): void {
  setDest((localStorage.getItem("rplay-vm-dest") as Dest) === "task" ? "task" : "mail");
  $("#vmDest")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-dest]");
    if (b) setDest(b.getAttribute("data-dest") === "task" ? "task" : "mail");
  });
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!b) return;
    switch (b.getAttribute("data-act")) {
      case "vmToggle": void vmToggle(); break;
      case "vmSend": void vmSend(); break;
      case "vmCancel": vmCancel(); break;
    }
  });
}
